import { useEffect, useState } from "react";
import { supabase } from "./supabase";
import type { Database } from "../types/db";

type Tables = Database["public"]["Tables"];
export type Account = Tables["google_ads_accounts"]["Row"];
export type Campaign = Tables["google_ads_campaigns"]["Row"];
export type CampaignMetricsDaily = Tables["google_ads_campaign_metrics_daily"]["Row"];
export type SyncRun = Tables["google_ads_sync_runs"]["Row"];

export type GoogleAdsData = {
  account: Account | null;
  campaigns: Campaign[];
  metrics: CampaignMetricsDaily[];
  syncRuns: SyncRun[];
  loading: boolean;
  error: string | null;
};

const LOOKBACK_DAYS = 30;

export function useGoogleAdsData(): GoogleAdsData {
  const [account, setAccount] = useState<Account | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [metrics, setMetrics] = useState<CampaignMetricsDaily[]>([]);
  const [syncRuns, setSyncRuns] = useState<SyncRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const client = supabase();
    let cancelled = false;

    async function fetchAll() {
      try {
        const since = new Date();
        since.setDate(since.getDate() - LOOKBACK_DAYS);
        const sinceIso = since.toISOString().slice(0, 10);

        const [a, c, m, r] = await Promise.all([
          client.from("google_ads_accounts").select("*").limit(1).maybeSingle(),
          client.from("google_ads_campaigns").select("*").order("name"),
          client.from("google_ads_campaign_metrics_daily").select("*").gte("date", sinceIso),
          client.from("google_ads_sync_runs").select("*").order("started_at", { ascending: false }).limit(20),
        ]);

        if (cancelled) return;
        if (a.error) throw a.error;
        if (c.error) throw c.error;
        if (m.error) throw m.error;
        if (r.error) throw r.error;

        setAccount(a.data ?? null);
        setCampaigns(c.data ?? []);
        setMetrics(m.data ?? []);
        setSyncRuns(r.data ?? []);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAll();

    const channel = client
      .channel("google-ads-data")
      .on("postgres_changes", { event: "*", schema: "public", table: "google_ads_accounts" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "google_ads_campaigns" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "google_ads_campaign_metrics_daily" }, fetchAll)
      .on("postgres_changes", { event: "*", schema: "public", table: "google_ads_sync_runs" }, fetchAll)
      .subscribe();

    return () => {
      cancelled = true;
      client.removeChannel(channel);
    };
  }, []);

  return { account, campaigns, metrics, syncRuns, loading, error };
}

export type Kpis = {
  impressions: number;
  clicks: number;
  costMicros: number;
  conversions: number;
  conversionsValue: number;
  ctr: number;
  avgCpcMicros: number;
};

export function aggregateKpis(rows: CampaignMetricsDaily[]): Kpis {
  let impressions = 0;
  let clicks = 0;
  let costMicros = 0;
  let conversions = 0;
  let conversionsValue = 0;
  for (const r of rows) {
    impressions += r.impressions;
    clicks += r.clicks;
    costMicros += r.cost_micros;
    conversions += r.conversions;
    conversionsValue += r.conversions_value;
  }
  return {
    impressions,
    clicks,
    costMicros,
    conversions,
    conversionsValue,
    ctr: impressions > 0 ? clicks / impressions : 0,
    avgCpcMicros: clicks > 0 ? Math.round(costMicros / clicks) : 0,
  };
}

export function aggregateByCampaign(
  campaigns: Campaign[],
  rows: CampaignMetricsDaily[],
): Array<Campaign & { kpis: Kpis }> {
  const byCampaign = new Map<string, CampaignMetricsDaily[]>();
  for (const r of rows) {
    const list = byCampaign.get(r.campaign_id) ?? [];
    list.push(r);
    byCampaign.set(r.campaign_id, list);
  }
  return campaigns.map((c) => ({
    ...c,
    kpis: aggregateKpis(byCampaign.get(c.id) ?? []),
  }));
}
